import { useState } from 'react'
import { Map, Trash2, Upload } from 'lucide-react'
import { Button, Divider, EmptyHint, Field } from '../ui/Panels'
import { StateBadge } from '../ui/State'
import { parseCoords } from '../pilot/SppiForm'
import { formatDecimal } from '../../lib/geo'
import type { Geofence } from '../../types'

/* ============================================================================
 * ГЕОЗОНЫ: загрузка и удаление
 *
 * GET    /api/geo          — список геозон
 * POST   /api/geo          — создать геозону
 * DELETE /api/geo/<uuid>   — удалить геозону
 *
 * Координаты задаются в WGS-84 в порядке «широта, долгота».
 * ========================================================================= */

const SRC_LABEL: Record<string, string> = {
  mintc: 'Минтранс №92',
  sppi: 'СППИ',
  own: 'своя',
}

export function GeofencePanel({
  geofences,
  onCreate,
  onDelete,
  busy,
}: {
  geofences: Geofence[]
  onCreate: (fence: Geofence) => void
  onDelete: (id: string) => void
  busy: boolean
}) {
  const [name, setName] = useState('')
  const [kind, setKind] = useState('ограничение')
  const [polyText, setPolyText] = useState('')
  // П.2 (2026-10-06): фильтр списка по источнику ('' = все)
  const [srcFilter, setSrcFilter] = useState<'' | 'mintc' | 'sppi' | 'own'>('')
  const shown = srcFilter
    ? geofences.filter((f) => (f.source ?? 'own') === srcFilter)
    : geofences
  const bySrc = geofences.reduce<Record<string, number>>((acc, f) => {
    const k = f.source ?? 'own'
    acc[k] = (acc[k] ?? 0) + 1
    return acc
  }, {})

  const polygon = parseCoords(polyText)
  const valid = name.trim() !== '' && polygon.length >= 3

  const submit = (): void => {
    if (!valid) return
    onCreate({
      name: name.trim(),
      kind,
      // Передаём в порядке GeoJSON: [долгота, широта]
      polygon: polygon.map(([lon, lat]) => [lon, lat] as [number, number]),
    })
    setName('')
    setPolyText('')
  }

  return (
    <div className="space-y-2 p-2.5">
      <Divider label="Создание геозоны" />

      <div className="grid grid-cols-2 gap-2">
        <Field label="Название">
          <input
            className="ut-input"
            placeholder="Зона над аэропортом"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </Field>
        <Field label="Тип">
          <select className="ut-select" value={kind} onChange={(e) => setKind(e.target.value)}>
            <option value="запрет">Запретная</option>
            <option value="ограничение">Ограниченная</option>
            <option value="контроль">Зона контроля</option>
            <option value="оповещение">Оповещения</option>
          </select>
        </Field>
      </div>

      <Field
        label="Контуры (WGS-84)"
        hint={`Строки «широта, долгота». Вершин: ${polygon.length} (минимум 3)`}
        error={polyText.trim() !== '' && polygon.length < 3 ? 'Минимум 3 вершины' : undefined}
      >
        <textarea
          className="ut-textarea"
          rows={4}
          placeholder={'55.7558, 37.6173\n55.7658, 37.6173\n55.7658, 37.6273'}
          value={polyText}
          onChange={(e) => setPolyText(e.target.value)}
        />
      </Field>

      <Button icon={Upload} variant="primary" size="sm" full disabled={!valid || busy} onClick={submit}>
        Создать геозону
      </Button>

      <Divider label={`Активные геозоны (${shown.length}/${geofences.length})`} />

      {/* П.2: фильтр по источнику с количеством по каждому */}
      {geofences.length > 0 && (
        <div className="flex items-center gap-1.5">
          <select
            className="ut-select text-[11px]"
            value={srcFilter}
            onChange={(e) => setSrcFilter(e.target.value as '' | 'mintc' | 'sppi' | 'own')}
          >
            <option value="">Все источники ({geofences.length})</option>
            <option value="mintc">Минтранс №92 ({bySrc.mintc ?? 0})</option>
            <option value="sppi">СППИ ({bySrc.sppi ?? 0})</option>
            <option value="own">Свои ({bySrc.own ?? 0})</option>
          </select>
        </div>
      )}

      {shown.length === 0 ? (
        <EmptyHint>
          <span className="flex flex-col items-center gap-1.5">
            <Map size={18} className="text-ink-muted" aria-hidden />
            Геозоны не заданы
          </span>
        </EmptyHint>
      ) : (
        <div className="space-y-1">
          {shown.map((f, i) => {
            const id = String(f.id ?? '')
            const isControl = String(f.kind ?? '').includes('контрол')
            const isNoFly = String(f.kind ?? '').includes('запрет')
            return (
              <div
                key={id || i}
                className={`flex items-center gap-2 rounded-md border px-2 py-1.5 ut-stated ${
                  isNoFly
                    ? 'ut-state-critical-error'
                    : isControl
                      ? 'ut-state-warning'
                      : 'ut-state-pending'
                }`}
              >
                <div className="min-w-0 flex-1">
                  <div className="truncate text-[11px] font-semibold">{f.name ?? `Геозона ${i + 1}`}</div>
                  <div className="ut-metric truncate text-[10px] text-ink-muted">
                    <span className="text-ink">
                      [{SRC_LABEL[f.source ?? 'own'] ?? 'своя'}]
                    </span>{' '}
                    {f.kind ?? '—'} · вершин {f.polygon?.length ?? 0}
                    {f.polygon && f.polygon.length > 0
                      ? ` · ${formatDecimal(f.polygon[0][1], f.polygon[0][0])}`
                      : ''}
                  </div>
                </div>
                <StateBadge state={isNoFly ? 'critical-error' : isControl ? 'warning' : 'pending'}>
                  {isNoFly ? 'ЗАПРЕТ' : isControl ? 'КОНТРОЛЬ' : 'ОГРАН.'}
                </StateBadge>
                {id && (
                  <Button
                    size="sm"
                    icon={Trash2}
                    variant="danger"
                    disabled={busy}
                    onClick={() => onDelete(id)}
                  />
                )}
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}
